import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@kognitos/lattice';
import noop from 'lodash/noop';
import { IDPPanelLoading } from '@/modules/idp/components/IDPPanelLoading';
import {
  EIDPSource,
  type IIDPField,
  type IIDPMissingField,
} from '@/modules/idp/types';
import { type TResolvedValue, ERenderMode } from '../../types';
import { IDPDocumentThumbnail } from './IDPDocumentThumbnail';

const IDPInlinePanel = lazy(() =>
  import('@/modules/idp/components/IDPInlinePanel').then(m => ({
    default: m.IDPInlinePanel,
  })),
);

/**
 * Props for the IDPPreviewRenderer component.
 */
interface IIDPPreviewRendererProps {
  /** Document resource name for fetching the file */
  documentName: string;
  /** Fields for OUTPUT mode */
  fields: IIDPField[];
  /** Rendering mode (compact, preview, expanded) */
  mode: ERenderMode;
  /** Closes the output modal; the expanded panel has no close of its own */
  onExpandedClose?: () => void;
  /** Makes the thumbnail clickable where no card wraps it (the classic chat widget). */
  onPreviewClick?: () => void;
  /** Exception fields for EXCEPTION mode (resolved exceptions) */
  exceptionFields?: IIDPMissingField[];
  /** Source context - defaults to OUTPUT */
  source?: EIDPSource;
  /** Total number of source documents (shows badge if > 1) */
  sourceCount?: number;
  /** Every extraction in a list or bundle, shown as tabs in the expanded panel */
  idpListItems?: Record<string, TResolvedValue>[];
}

/**
 * Renders an IDP extraction. As a card body it is the document's first page
 * with a confidence-coloured box over each field; in the output modal it is
 * the IDP panel itself, the document beside its extracted fields, read only.
 *
 * @param props - Component props
 */
export function IDPPreviewRenderer(props: IIDPPreviewRendererProps) {
  const {
    documentName,
    fields,
    mode,
    onExpandedClose,
    onPreviewClick,
    exceptionFields,
    source = EIDPSource.OUTPUT,
    sourceCount = 1,
    idpListItems,
  } = props;
  const { t } = useTranslation('outputs');

  const isExpanded = mode === ERenderMode.EXPANDED;

  if (isExpanded) {
    return (
      <Suspense fallback={<IDPPanelLoading />}>
        <IDPInlinePanel
          embedded
          readOnly
          className="mb-0 rounded-none border-0 bg-transparent shadow-none"
          onClose={onExpandedClose ?? noop}
          documentName={documentName}
          fields={fields}
          source={source}
          exceptionFields={exceptionFields}
          idpListItems={idpListItems}
        />
      </Suspense>
    );
  }

  const thumbnail = (
    <IDPDocumentThumbnail
      documentName={documentName}
      fields={fields}
      className="h-[180px]"
    />
  );
  return (
    <div className="relative overflow-hidden rounded-lg bg-card">
      {onPreviewClick ? (
        <Button
          variant="ghost"
          className="block h-auto w-full p-0 not-disabled:hover:bg-transparent"
          onClick={onPreviewClick}
          aria-label={t('outputs.idp.viewDocument')}
        >
          {thumbnail}
        </Button>
      ) : (
        thumbnail
      )}

      {sourceCount > 1 && (
        <div className="absolute bottom-2 right-2 z-40 pointer-events-none">
          <Badge variant="secondary" className="text-xs shadow-sm">
            {sourceCount} {t('outputs.idp.sources')}
          </Badge>
        </div>
      )}
    </div>
  );
}
